// ─────────────────────────────────────────────────────────────────────────────
// lib/badges.js — logica pura dei badge (nessuna UI, nessuna chiamata al DB)
//
// Il catalogo sta in dim_badge (un badge = una riga; una "famiglia" = tutte le righe con la stessa category, i suoi livelli).
// buildFamilies unisce catalogo, badge ottenuti, avanzamento e statistiche di lega in una lista di famiglie pronte da mostrare.
// ─────────────────────────────────────────────────────────────────────────────
import { RANK_COLORS } from './rankColors'

export const BADGE_GROUPS = ['gt', 'monuments', 'picks', 'consistency', 'league', 'social', 'meta']

// Colori dei livelli: bronzo, argento, oro (come il podio) e platino per il quarto livello
export const TIER_COLORS = [RANK_COLORS[3], RANK_COLORS[2], RANK_COLORS[1], '#67E8F9']

// Colore di un livello: i badge a livello unico (traguardi) sono sempre oro
export function tierColor(level, maxLevel) {
  if (maxLevel <= 1) return RANK_COLORS[1]
  return TIER_COLORS[Math.min(Math.max(level, 1), TIER_COLORS.length) - 1]
}

export const ROMAN = ['I', 'II', 'III', 'IV', 'V']

// catalog: righe di dim_badge; earned: righe di fact_user_badge (id_badge, awarded_at, season_year);
// progress: righe di fact_user_badge_progress (category, season_year, value); stats: Map id_badge -> {n_earned, n_members}
export function buildFamilies(catalog, earned, progress, stats) {
  const earnedBy = new Map()
  for (const e of earned) {
    if (!earnedBy.has(e.id_badge)) earnedBy.set(e.id_badge, [])
    earnedBy.get(e.id_badge).push(e)
  }
  const progBy = new Map()   // category -> valore dell'ultima stagione (o 0 se non annuale)
  for (const p of progress) {
    const cur = progBy.get(p.category)
    if (!cur || p.season_year > cur.season_year) progBy.set(p.category, p)
  }
  const fams = new Map()
  for (const b of [...catalog].sort((a, c) => a.sort_order - c.sort_order)) {
    if (!fams.has(b.category)) {
      fams.set(b.category, { category: b.category, group: b.badge_group, icon: b.icon_key, yearly: b.is_yearly, secret: b.is_secret, sort: b.sort_order, levels: [] })
    }
    const rows = (earnedBy.get(b.id_badge) ?? []).slice().sort((x, y) => String(y.awarded_at).localeCompare(String(x.awarded_at)))
    const st = stats?.get(b.id_badge)
    fams.get(b.category).levels.push({
      id_badge: b.id_badge, code: b.code, level: b.level, threshold: b.threshold,
      earned: rows.length > 0, rows, awardedAt: rows[0]?.awarded_at ?? null, seasons: rows.length,
      nEarned: st?.n_earned ?? 0, nMembers: st?.n_members ?? 0,
    })
  }
  return [...fams.values()].map(f => {
    f.levels.sort((a, b) => a.level - b.level)
    const maxLevel = f.levels.length
    const top = [...f.levels].reverse().find(l => l.earned) ?? null          // livello piu' alto ottenuto
    const next = f.levels.find(l => !l.earned) ?? null                       // primo livello non ottenuto
    const value = progBy.get(f.category)?.value ?? 0
    const target = next ?? top
    return {
      ...f, maxLevel, top, next, value,
      earned: !!top,
      complete: f.yearly ? false : !next,
      seasons: f.yearly ? (top?.seasons ?? 0) : 0,
      // quanto manca al prossimo livello, da 0 a 1 (per le barre e per i "traguardi vicini")
      ratio: next ? Math.min(1, value / next.threshold) : 1,
      target,
    }
  })
}

// Quanti badge distinti ha ottenuto (ogni riga di dim_badge conta una volta, anche se annuale e ripetuto)
export const countEarned = (families) => families.reduce((n, f) => n + f.levels.filter(l => l.earned).length, 0)
export const countTotal = (families) => families.reduce((n, f) => n + f.levels.length, 0)

// Famiglie piu' vicine al prossimo livello (almeno meta' strada), per il riepilogo
export function nearGoals(families, limit = 2) {
  return families
    .filter(f => !f.complete && !f.yearly && f.next && f.value > 0 && f.ratio >= 0.4 && !(f.secret && !f.earned))
    .sort((a, b) => b.ratio - a.ratio)
    .slice(0, limit)
}

// Ultimi badge ottenuti (per data), con la famiglia di appartenenza
export function recentEarned(families, limit = 4) {
  const out = []
  for (const f of families) for (const l of f.levels) if (l.earned) out.push({ family: f, level: l })
  return out.sort((a, b) => String(b.level.awardedAt).localeCompare(String(a.level.awardedAt))).slice(0, limit)
}

// Colori delle maglie dei Grandi Giri (rosa del Giro, gialla del Tour, rossa della Vuelta)
export const GT_JERSEYS = [
  { key: 'giro',   category: 'DAYS_PINK',   color: '#EC4899', label: 'badges.jersey_pink' },
  { key: 'tour',   category: 'DAYS_YELLOW', color: '#FACC15', label: 'badges.jersey_yellow' },
  { key: 'vuelta', category: 'DAYS_RED',    color: '#DC2626', label: 'badges.jersey_red' },
]

// Giorni in maglia per Grande Giro dall'avanzamento (righe di fact_user_badge_progress)
export function jerseyDays(progress) {
  const out = {}
  for (const j of GT_JERSEYS) out[j.key] = progress.filter(p => p.category === j.category && p.season_year === 0).reduce((m, p) => Math.max(m, p.value), 0)
  return out
}
